// frontend/src/features/portfolio/pages/PortfolioDetailPage.jsx
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import { ArrowLeft, MessageSquare } from 'lucide-react';
import { fetchPortfolioSummary } from '../lib/portfolioSummary';
import { setLastAccessedPortfolio } from '../lib/lastAccessedPortfolio';
import PortfolioSummaryView from '../components/PortfolioSummaryView';
import FieldInsightPanel from '../../../components/FieldInsightPanel';
import * as fieldInsightStore from '../../../utils/fieldInsightStore';

const PortfolioDetailPage = () => {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const { fetchWithAuth, recordActivity } = useAuth();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [insightOpen, setInsightOpen] = useState(false);
  const [focusNonce, setFocusNonce] = useState(0);

  const fallbackName = state?.name || 'Portfolio';
  const textPrimary = darkMode ? '#FFFFFF' : '#0A0A0A';
  const textMuted = darkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)';

  const sessionKey = 'portfolio:' + id;

  // Stay-mounted guard so a late/slow resolve can't setState after navigation away.
  // Re-armed inside the effect (not just at useRef init) because StrictMode's dev
  // double-invoke runs the cleanup once on this same instance - a latched `false`
  // would silently drop the fetch resolution and pin the page on "Loading ...".
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Erase the shared chat session when leaving this portfolio (unmount) or
  // switching to a different client id. History lives only in-memory.
  useEffect(() => () => fieldInsightStore.clearSession('portfolio:' + id), [id]);

  const load = useCallback((force = false) => {
    setLoading(true);
    setError(null);
    fetchPortfolioSummary(id, { force })
      .then((d) => {
        if (mountedRef.current) { setData(d); setLoading(false); }
        // Record the viewed client so the report picker can autofill it. Needs a
        // codename (the value the picker resolves against); skip if absent.
        if (d && d.codename) {
          setLastAccessedPortfolio({ codename: d.codename, name: d.name, clientId: id });
        }
      })
      .catch((e) => { if (mountedRef.current) { setError(e); setLoading(false); } });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Build the portfolio-surface FieldContext, remember it on the session, open
  // the panel and refocus its input. Double-click also kicks off the explain run.
  const onField = useCallback((payload, isDouble) => {
    const field = {
      surface: 'portfolio',
      report_type: null,
      account_code: null,
      account_name: data ? data.name : null,
      client_id: Number(id),
      codename: data ? data.codename : null,
      as_of_date: data ? data.as_of_date : null,
      section: null,
      column_label: null,
      row_description: null,
      field_label: payload.field_label,
      field_value: payload.field_value,
      field_value_raw: payload.field_value_raw,
      // The whole on-screen summary payload (small): the chatbot answers from
      // the same numbers the user sees, without re-querying the lake.
      report_data: data ? JSON.stringify(data).slice(0, 60000) : null,
    };
    if (isDouble) {
      fieldInsightStore.startExplain(sessionKey, { fetchWithAuth, recordActivity, field });
    } else {
      fieldInsightStore.setField(sessionKey, field);
    }
    setInsightOpen(true);
    setFocusNonce((n) => n + 1);
  }, [data, id, sessionKey, fetchWithAuth, recordActivity]);

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      {/* Open the field-insight chat without double-clicking a field. Hidden
          while the panel is open (it occupies the top-right). */}
      {!insightOpen && (
        <button
          onClick={() => { setInsightOpen(true); setFocusNonce((n) => n + 1); }}
          title="Chat"
          style={{
            position: 'absolute', top: 24, right: 24, zIndex: 5,
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 13px', borderRadius: 9999,
            border: `1px solid ${darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
            background: darkMode ? '#141414' : '#FFFFFF',
            color: textPrimary, cursor: 'pointer', fontSize: 13,
            fontFamily: 'Inter, sans-serif',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = darkMode ? '#1c1c1c' : '#f5f5f5')}
          onMouseLeave={(e) => (e.currentTarget.style.background = darkMode ? '#141414' : '#FFFFFF')}
        >
          <MessageSquare size={15} />
          Chat
        </button>
      )}
      {/* While the insight panel is open, yield its strip (16px inset +
          min(420px, 40%) panel + 16px gap) so the title and metrics reflow
          left of the panel instead of rendering under it. */}
      <div style={{ padding: 24, paddingRight: insightOpen ? 'calc(min(420px, 40%) + 32px)' : 24, fontFamily: 'Inter, sans-serif', height: '100%', boxSizing: 'border-box', color: textPrimary, overflowY: 'auto' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: 0, marginBottom: 20,
            border: 'none', background: 'transparent', color: textMuted, fontSize: 14,
            fontFamily: 'Inter, sans-serif', cursor: 'pointer' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = textPrimary)}
          onMouseLeave={(e) => (e.currentTarget.style.color = textMuted)}
        >
          <ArrowLeft size={14} />
          Portfolios
        </button>

        {loading && <p style={{ color: textMuted, fontSize: 14 }}>Loading {fallbackName}…</p>}

        {!loading && error && (
          <div>
            <h1 className="font-display" style={{ fontSize: 'clamp(1.5rem,3vw,2.25rem)', fontWeight: 400, margin: 0 }}>
              {fallbackName}
            </h1>
            <p style={{ color: textMuted, fontSize: 14, marginTop: 12 }}>
              Couldn't load this portfolio.{' '}
              <button onClick={() => load(true)} style={{ background: 'none', border: 'none', color: textPrimary, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                Retry
              </button>
            </p>
          </div>
        )}

        {!loading && !error && data && <PortfolioSummaryView data={data} onField={onField} />}
      </div>

      {/* Wrapper stays click-through: the panel's transparent chrome lets clicks
          fall to the portfolio fields underneath, and the panel re-enables pointer
          events on its own interactive parts (messages, input, minimize). */}
      <div style={{ position: 'absolute', right: 16, top: 24, bottom: 24, width: 'min(420px, 40%)', pointerEvents: 'none' }}>
        <FieldInsightPanel
          sessionKey={sessionKey}
          darkMode={darkMode}
          visible={insightOpen}
          focusNonce={focusNonce}
          onMinimize={() => setInsightOpen(false)}
          fetchWithAuth={fetchWithAuth}
          recordActivity={recordActivity}
          placeholder="Ask about this portfolio..."
        />
      </div>
    </div>
  );
};

export default PortfolioDetailPage;
